import { useAppDispatch, useAppSelector } from '@app/hooks';
import { useInputErrors } from '@app/hooks/useInputErrors';
import { isEmpty } from '@app/util/validations';
import { Button, Col, Grid, RSelect, Title } from '@common/components';
import {
  GROUPING_CLASS,
  GROUPING_NONE,
} from '@dashboard/constants/identification';
import { selectSelectedCenters } from '@dashboard/slices/filters';
import {
  selectData,
  setSelectedCenterToShow,
  setSelectedClassToShow,
} from '@dashboard/slices/identificationStats';
import PropTypes from 'prop-types';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import viewClasses from './IdentificationDataSelectionForm.module.scss';

export default function IdentificationDataSelectionForm({ onSubmit }) {
  const { t } = useTranslation('dashboard\\IdentificationStatsView');
  const { t: selectT } = useTranslation('_common\\Select');
  const { errors, resetErrors, updateErrors } = useInputErrors();
  const dispatch = useAppDispatch();
  const {
    selectedCenterToShow,
    selectedClassToShow,
    selectedCenterClassesMap,
    selectedGrouping,
  } = useAppSelector(selectData);

  const currentFilteredCenters = useAppSelector(selectSelectedCenters);

  const centerOptions = useMemo(() => {
    return currentFilteredCenters
      .map((center) => ({
        label: center.name,
        value: center.uuid,
      }))
      .filter(
        (centerOption) =>
          selectedCenterClassesMap[centerOption.value]?.length > 0,
      );
  }, [currentFilteredCenters, selectedCenterClassesMap]);

  const allClassesOption = { label: selectT('all'), value: 'all' };

  const classOptions = useMemo(() => {
    const options = [allClassesOption];
    options.push(
      ...(selectedCenterClassesMap[selectedCenterToShow?.value] || []),
    );
    return options;
  }, [selectedCenterClassesMap, selectedCenterToShow]);

  const handleSelectCenter = (option) => {
    dispatch(setSelectedCenterToShow(option));
    dispatch(setSelectedClassToShow(null)); // Reset class selection when center changes
  };

  const handleSelectClass = (option) => {
    if (option.value === 'all') {
      option = null;
    }
    dispatch(setSelectedClassToShow(option));
  };

  const validate = () => {
    let isValid = resetErrors();

    if (isEmpty(selectedCenterToShow)) {
      isValid = updateErrors({ center: t('centerValidationRequired') });
    }

    return isValid;
  };

  const handleSubmit = () => {
    const isValid = validate();
    if (!isValid) {
      return; // Prevent submission if validation fails
    }
    onSubmit();
  };

  if (selectedGrouping === GROUPING_NONE || !selectedGrouping) {
    return null; // Do not render the form if grouping is set to none
  }

  return (
    <Grid justify="end" align="middle">
      <Col>
        <Title type="h3" color="grey-400">
          {t('viewDataFor')}
        </Title>
      </Col>
      <Col xss={12} xs={12} sm={3}>
        <RSelect
          containerClassName={viewClasses.selectContainer}
          name="center"
          data={centerOptions}
          onChange={handleSelectCenter}
          val={selectedCenterToShow}
          placeholder={t('center')}
          error={errors.center}
        />
      </Col>

      {selectedGrouping === GROUPING_CLASS ? (
        <Col xss={12} xs={12} sm={3}>
          <RSelect
            containerClassName={viewClasses.selectContainer}
            data={classOptions}
            onChange={handleSelectClass}
            val={
              selectedClassToShow === null
                ? allClassesOption
                : selectedClassToShow
            }
            placeholder={t('classGroup')}
          />
        </Col>
      ) : null}

      <Col>
        <Button color="accent" variant="flat" onClick={handleSubmit}>
          {t('showData')}
        </Button>
      </Col>
    </Grid>
  );
}

IdentificationDataSelectionForm.propTypes = {
  onSubmit: PropTypes.func.isRequired,
};
